import { useTranslation } from 'react-i18next';
import { Field, Panel, Select } from '@/shared/ui';
import { TextField } from '@/shared/forms';
import { WebFilterFileSlot } from '@/api/generated/schemas';
import type { WebFilterFormApi } from '../useWebFilterForm';
import { AssetFileField } from './AssetFileField';

const LABEL_W = 140;

export interface FilterIdentityPanelProps {
  form: WebFilterFormApi;
  /** Create: project editable (select). Edit: read-only. */
  webProjectOptions: { value: string; label: string }[];
  projectImmutable: boolean;
}

/** Panel „Filter" — název, ikona, webový projekt. */
export function FilterIdentityPanel({
  form,
  webProjectOptions,
  projectImmutable,
}: FilterIdentityPanelProps) {
  const { t } = useTranslation('www');

  return (
    <Panel variant="filled" icon="filter" title={t('webFilter.identity.title')}>
      <div className="gap-phx-3 flex flex-col">
        <form.Field name="name">
          {(field) => (
            <TextField
              field={field}
              layout="inline"
              labelWidth={LABEL_W}
              label={t('webFilter.identity.name')}
              required
              data-testid="web-filter-name"
            />
          )}
        </form.Field>

        <form.Subscribe selector={(s) => s.values.webProjectId}>
          {(webProjectId) => (
            <form.Field name="iconFileId">
              {(field) => (
                <Field layout="inline" labelWidth={LABEL_W} label={t('webFilter.identity.icon')}>
                  <AssetFileField
                    fileId={field.state.value}
                    webProjectId={webProjectId}
                    slot={WebFilterFileSlot.Icon}
                    accept=".svg,.png,image/svg+xml,image/png"
                    emptyLabel={t('webFilter.identity.noIcon')}
                    uploadLabel={t('webFilter.identity.uploadIcon')}
                    clearLabel={t('webFilter.identity.clearIcon')}
                    previewClassName="h-10 w-10"
                    onUploaded={(fileId) => field.handleChange(fileId)}
                    onClear={() => field.handleChange(null)}
                  />
                </Field>
              )}
            </form.Field>
          )}
        </form.Subscribe>

        <form.Field name="webProjectId">
          {(field) => (
            <Field
              layout="inline"
              labelWidth={LABEL_W}
              label={t('webFilter.identity.webProject')}
              required
            >
              {projectImmutable ? (
                <form.Subscribe selector={(s) => s.values.webProjectName}>
                  {(name) => (
                    <span className="text-phx-sm text-phx-text" data-testid="web-filter-project-ro">
                      {name || `#${field.state.value}`}
                    </span>
                  )}
                </form.Subscribe>
              ) : (
                <Select
                  value={field.state.value ? String(field.state.value) : ''}
                  options={webProjectOptions}
                  placeholder={t('webFilter.identity.webProjectPlaceholder')}
                  invalid={field.state.meta.errors.length > 0}
                  onChange={(event) => {
                    const id = Number(event.target.value);
                    field.handleChange(Number.isFinite(id) && id > 0 ? id : 0);
                    const label =
                      webProjectOptions.find((o) => o.value === event.target.value)?.label ?? '';
                    form.setFieldValue('webProjectName', label);
                  }}
                  data-testid="web-filter-project"
                />
              )}
            </Field>
          )}
        </form.Field>
      </div>
    </Panel>
  );
}
